// ══════════════════════════════════════════════════════════════════
// Tablero de "Mis tareas": secciones configurables por usuario.
//
// Modelo híbrido:
//   · Cada columna tiene una REGLA (vence hoy, vencidas, sin fecha...) que
//     clasifica las tareas automáticamente.
//   · El usuario puede ARRASTRAR una tarea a la columna que quiera; esa
//     asignación manual gana sobre la regla hasta que la quite.
//
// Se guarda sólo la configuración del tablero (pocas columnas + el mapa de
// asignaciones manuales) en users/{uid}/preferences/myTasksBoard. Los
// documentos de las tareas NO se tocan: el tablero es personal, y la misma
// tarea la pueden ver varias personas con tableros distintos.
// ══════════════════════════════════════════════════════════════════
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { isToday, isPast, isBefore, startOfDay, addWeeks } from 'date-fns';

const PREF_DOC = (userId) => doc(db, `users/${userId}/preferences`, 'myTasksBoard');

// ── Fechas ──────────────────────────────────────────────────────────
// Mismo parseo que usa MyTasks: las fechas llegan como string ISO o Timestamp.
const parseDate = (value) => {
    if (!value) return null;
    try {
        if (typeof value === 'string') return new Date(value);
        if (value?.toDate) return value.toDate();
        if (value instanceof Date) return value;
        return null;
    } catch {
        return null;
    }
};

// ── Catálogo de reglas ──────────────────────────────────────────────
// `match` recibe (task, ctx). Las tareas finalizadas se filtran antes de
// llegar aquí (salvo en 'done' y 'all'), así el orden de las columnas no
// cambia dónde caen las completadas.
export const RULES = [
    {
        id: 'overdue', label: 'Vencidas', hint: 'La fecha límite ya pasó',
        match: (t, ctx) => { const d = parseDate(t.fechaVencimiento); return !!d && isPast(d) && !isToday(d) && d < ctx.today; }
    },
    {
        id: 'today', label: 'Vence hoy', hint: 'La fecha límite es hoy',
        match: (t) => { const d = parseDate(t.fechaVencimiento); return !!d && isToday(d); }
    },
    {
        id: 'thisWeek', label: 'Esta semana', hint: 'Vence dentro de los próximos 7 días',
        match: (t, ctx) => { const d = parseDate(t.fechaVencimiento); return !!d && !isToday(d) && !isPast(d) && isBefore(d, ctx.nextWeekEnd); }
    },
    {
        id: 'later', label: 'Más tarde', hint: 'Vence después de esta semana',
        match: (t, ctx) => { const d = parseDate(t.fechaVencimiento); return !!d && !isToday(d) && !isPast(d) && !isBefore(d, ctx.nextWeekEnd); }
    },
    {
        id: 'noDate', label: 'Sin fecha', hint: 'No tiene fecha límite',
        match: (t) => !parseDate(t.fechaVencimiento)
    },
    {
        id: 'done', label: 'Completadas', hint: 'Tareas terminadas',
        match: (t) => t.estado === 'finalizado'
    },
    {
        id: 'recurring', label: 'Recurrentes', hint: 'Tareas que se repiten',
        match: (t) => !!t.recurrence && t.recurrence !== 'none'
    },
    {
        id: 'priorityAlta', label: 'Prioridad alta', hint: 'Marcadas como alta',
        match: (t) => t.prioridad === 'alta'
    },
    {
        id: 'priorityMedia', label: 'Prioridad media', hint: 'Marcadas como media',
        match: (t) => t.prioridad === 'media'
    },
    {
        id: 'priorityBaja', label: 'Prioridad baja', hint: 'Marcadas como baja',
        match: (t) => t.prioridad === 'baja'
    },
    {
        id: 'inProgress', label: 'En curso', hint: 'Estado "en curso"',
        match: (t) => t.estado === 'en-curso'
    },
    {
        id: 'manual', label: 'Sólo manual', hint: 'Sólo lo que arrastres aquí',
        match: () => false
    },
    {
        id: 'all', label: 'Todo lo demás', hint: 'Cajón para lo que no encaje en otra columna',
        match: () => true
    },
];

export const RULE_BY_ID = RULES.reduce((acc, r) => { acc[r.id] = r; return acc; }, {});
export const getRuleLabel = (ruleId) => RULE_BY_ID[ruleId]?.label || 'Sin regla';

// ── Colores disponibles para las secciones ──────────────────────────
export const COLUMN_COLORS = {
    red: { dot: 'bg-red-500', badge: 'bg-red-100 text-red-700', border: 'border-red-200/60', ring: 'ring-red-300', soft: 'bg-red-50' },
    orange: { dot: 'bg-orange-500', badge: 'bg-orange-100 text-orange-700', border: 'border-orange-200/60', ring: 'ring-orange-300', soft: 'bg-orange-50' },
    amber: { dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700', border: 'border-amber-200/60', ring: 'ring-amber-300', soft: 'bg-amber-50' },
    green: { dot: 'bg-green-500', badge: 'bg-green-100 text-green-700', border: 'border-green-200/60', ring: 'ring-green-300', soft: 'bg-green-50' },
    teal: { dot: 'bg-teal-500', badge: 'bg-teal-100 text-teal-700', border: 'border-teal-200/60', ring: 'ring-teal-300', soft: 'bg-teal-50' },
    blue: { dot: 'bg-blue-500', badge: 'bg-blue-100 text-blue-700', border: 'border-blue-200/60', ring: 'ring-blue-300', soft: 'bg-blue-50' },
    indigo: { dot: 'bg-indigo-500', badge: 'bg-indigo-100 text-indigo-700', border: 'border-indigo-200/60', ring: 'ring-indigo-300', soft: 'bg-indigo-50' },
    purple: { dot: 'bg-purple-500', badge: 'bg-purple-100 text-purple-700', border: 'border-purple-200/60', ring: 'ring-purple-300', soft: 'bg-purple-50' },
    pink: { dot: 'bg-pink-500', badge: 'bg-pink-100 text-pink-700', border: 'border-pink-200/60', ring: 'ring-pink-300', soft: 'bg-pink-50' },
    gray: { dot: 'bg-gray-400', badge: 'bg-gray-100 text-gray-600', border: 'border-gray-200/60', ring: 'ring-gray-300', soft: 'bg-gray-50' },
};
export const COLOR_KEYS = Object.keys(COLUMN_COLORS);
export const getColors = (color) => COLUMN_COLORS[color] || COLUMN_COLORS.gray;

// ── Configuración por defecto ───────────────────────────────────────
// Reproduce exactamente las 6 columnas que el tablero tenía antes, para que
// nadie note un cambio al entrar por primera vez.
export const DEFAULT_COLUMNS = [
    { id: 'overdue', title: 'Vencidas', color: 'red', rule: 'overdue' },
    { id: 'today', title: 'Para hoy', color: 'blue', rule: 'today' },
    { id: 'thisWeek', title: 'Esta semana', color: 'indigo', rule: 'thisWeek' },
    { id: 'later', title: 'Más tarde', color: 'gray', rule: 'later' },
    { id: 'noDate', title: 'Sin fecha', color: 'gray', rule: 'noDate' },
    { id: 'done', title: 'Completadas', color: 'green', rule: 'done' },
];

export const DEFAULT_CONFIG = {
    columns: DEFAULT_COLUMNS,
    assignments: {},
    hideEmpty: false,
};

export const newColumnId = () => `col-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

// Normaliza lo que venga de Firestore para que el resto del código no tenga
// que defenderse de configuraciones a medio guardar.
const normalize = (data) => {
    if (!data || !Array.isArray(data.columns) || data.columns.length === 0) return { ...DEFAULT_CONFIG };
    return {
        columns: data.columns
            .filter(c => c && c.id)
            .map(c => ({
                id: c.id,
                title: c.title || 'Sección',
                color: COLUMN_COLORS[c.color] ? c.color : 'gray',
                rule: RULE_BY_ID[c.rule] ? c.rule : 'manual',
            })),
        assignments: data.assignments && typeof data.assignments === 'object' ? data.assignments : {},
        hideEmpty: !!data.hideEmpty,
    };
};

// ── Persistencia ────────────────────────────────────────────────────
export const subscribeToBoardConfig = (userId, callback) => {
    if (!userId) { callback({ ...DEFAULT_CONFIG }); return () => { }; }
    return onSnapshot(
        PREF_DOC(userId),
        (snap) => callback(snap.exists() ? normalize(snap.data()) : { ...DEFAULT_CONFIG }),
        (error) => {
            console.error('Error al leer la configuración del tablero:', error);
            callback({ ...DEFAULT_CONFIG });
        }
    );
};

export const saveBoardConfig = async (userId, config) => {
    if (!userId) return;
    const clean = normalize(config);
    await setDoc(PREF_DOC(userId), { ...clean, updatedAt: serverTimestamp() }, { merge: false });
};

// Quita del mapa las asignaciones de tareas que ya no existen y las que
// apuntan a columnas borradas. Evita que el documento crezca sin control.
export const pruneAssignments = (config, tasks) => {
    const validTaskIds = new Set(tasks.map(t => t.id));
    const validColumnIds = new Set(config.columns.map(c => c.id));
    const pruned = {};
    let changed = false;
    for (const [taskId, columnId] of Object.entries(config.assignments || {})) {
        if (validTaskIds.has(taskId) && validColumnIds.has(columnId)) pruned[taskId] = columnId;
        else changed = true;
    }
    return { pruned, changed };
};

// ── Resolución: qué tarea va en qué columna ─────────────────────────
// Orden de decisión:
//   1. Si está finalizada y existe una columna con regla 'done', va ahí
//      (completar una tarea manda sobre el arrastre: es la señal más fuerte).
//   2. Si la arrastraste a una columna que aún existe, va ahí.
//   3. Primera columna, en orden, cuya regla haga match.
//   4. Si nada hace match, la columna con regla 'all'; si no hay, se descarta.
export const resolveBoard = (tasks, config) => {
    const columns = config.columns || DEFAULT_COLUMNS;
    const assignments = config.assignments || {};
    const ctx = { today: startOfDay(new Date()), nextWeekEnd: addWeeks(startOfDay(new Date()), 1) };

    const buckets = columns.map(col => ({ ...col, tasks: [] }));
    const byId = new Map(buckets.map(b => [b.id, b]));
    const doneColumn = buckets.find(b => b.rule === 'done');
    const catchAll = buckets.find(b => b.rule === 'all');

    for (const task of tasks) {
        const isDone = task.estado === 'finalizado';

        if (isDone && doneColumn) { doneColumn.tasks.push(task); continue; }

        const manual = assignments[task.id] ? byId.get(assignments[task.id]) : null;
        if (manual) { manual.tasks.push(task); continue; }

        // Las completadas sin columna 'done' sólo pueden caer en el cajón general.
        let placed = false;
        if (!isDone) {
            for (const bucket of buckets) {
                if (bucket.rule === 'done' || bucket.rule === 'manual' || bucket.rule === 'all') continue;
                if (RULE_BY_ID[bucket.rule]?.match(task, ctx)) { bucket.tasks.push(task); placed = true; break; }
            }
        }
        if (!placed && catchAll) catchAll.tasks.push(task);
    }

    return buckets;
};

// ── Valores que hereda una tarea creada desde una columna ───────────
// Si añades una tarea desde "Para hoy", nace con fecha de hoy. Así el
// "+ Agregar tarea" de cada columna hace lo que uno espera.
export const defaultsForColumn = (column) => {
    if (!column) return {};
    const today = startOfDay(new Date());
    switch (column.rule) {
        case 'today':
            return { fechaVencimiento: today.toISOString() };
        case 'thisWeek':
            return { fechaVencimiento: addWeeks(today, 1).toISOString() };
        case 'priorityAlta':
            return { prioridad: 'alta' };
        case 'priorityMedia':
            return { prioridad: 'media' };
        case 'priorityBaja':
            return { prioridad: 'baja' };
        default:
            return {};
    }
};
